import { Info, Play, Terminal } from "lucide-react";
import React, { useEffect, useState } from "react";
import jobsConfig from "../../../config/jobsConfig";
import { useTheme } from "../../../contexts/ThemeContext";
import jobService from "../../../services/JobService";
import RunJobModal from "./RunJobModal";

interface JobListProps {
  refreshTrigger?: number;
}

const JobList: React.FC<JobListProps> = ({ refreshTrigger = 0 }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const [jobNames, setJobNames] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedJob, setSelectedJob] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [timeTick, setTimeTick] = useState(0);

  // Force re-render every minute to update time-based restrictions
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeTick(t => t + 1);
    }, 1000 * 60);
    return () => clearInterval(timer);
  }, []);

  const fetchJobNames = async () => {
    try {
      setLoading(true);
      const backendNames = await jobService.getJobNames();
      // Merge backend jobs with additional jobs from config, removing duplicates
      const mergedNames = Array.from(new Set(backendNames.concat(jobsConfig.additionalJobs)));
      // Sort alphabetically for consistent display
      mergedNames.sort((a, b) => a.localeCompare(b));
      setJobNames(mergedNames);
      setError(null);
    } catch (err: any) {
      setError(err.message || "Failed to fetch job names");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobNames();
  }, [refreshTrigger]);

  const handleRunClick = (jobName: string) => {
    setSelectedJob(jobName);
    setIsModalOpen(true);
  };

  const handleJobSuccess = () => {
    setIsModalOpen(false);
    // Could trigger a parent refresh if needed, but for now just close modal
  };

  // Helper to check if current time is restricted (1:30 PM - 5:00 PM PST)
  const isRestrictedTime = () => {
    try {
      const now = new Date();
      
      // Formatter for logic (24h)
      const logicFormatter = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/Los_Angeles",
        hour: "numeric",
        minute: "numeric",
        hour12: false,
      });
      
      const parts = logicFormatter.formatToParts(now);
      const hourPart = parts.find(p => p.type === "hour");
      const minutePart = parts.find(p => p.type === "minute");
      
      if (!hourPart || !minutePart) return false;
      
      const hours = parseInt(hourPart.value, 10);
      const minutes = parseInt(minutePart.value, 10);

      const currentTime = hours * 60 + minutes;
      const startTime = 13 * 60 + 30; // 13:30
      const endTime = 17 * 60; // 17:00

      return currentTime >= startTime && currentTime <= endTime;
    } catch (e) {
      console.error("Error checking restricted time:", e);
      return false;
    }
  };

  // Calculate restriction once per render
  const isCurrentTimeRestricted = isRestrictedTime();

  if (loading) {
    return (
      <div className={`${isDarkMode ? "bg-rp-surface" : "bg-white"} rounded-lg shadow p-6 flex justify-center`}>
        <div
          className={`animate-spin rounded-full h-8 w-8 border-b-2 ${isDarkMode ? "border-rp-amber" : "border-blue-500"}`}
        ></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${isDarkMode ? "bg-rp-surface" : "bg-white"} rounded-lg shadow p-6`}>
        <div className={`text-center ${isDarkMode ? "text-red-400" : "text-red-600"}`}>
          Error: {error}
        </div>
      </div>
    );
  }

  return (
    <div className={`${isDarkMode ? "bg-rp-surface" : "bg-white"} rounded-lg shadow`}>
      <div className={`px-6 py-4 border-b ${isDarkMode ? "border-rp-separator" : "border-gray-200"}`}>
        <h2 className={`text-lg font-medium ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>Available Jobs</h2>
      </div>
      <div className={`divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}>
        {jobNames.length === 0 ? (
          <div className={`p-6 text-center ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`}>No jobs found.</div>
        ) : (
          jobNames.map(jobName => {
            const isRestrictedJob = jobsConfig.restrictedJobs.includes(jobName);
            const isRestricted = isRestrictedJob && isCurrentTimeRestricted;
            
            return (
              <div key={jobName} className={`flex items-center justify-between px-6 py-4 transition-colors ${
                isDarkMode ? "hover:bg-rp-surface-raised" : "hover:bg-gray-50"
              }`}>
                <div className="flex items-center">
                  <div className={`p-2 rounded-lg mr-3 ${isDarkMode ? "bg-rp-surface-raised text-blue-400" : "bg-blue-50 text-blue-600"}`}>
                    <Terminal className="w-5 h-5" />
                  </div>
                  <div className="flex items-center">
                    <h3 className={`text-sm font-medium ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>{jobName}</h3>
                    {isRestrictedJob && (
                      <div className="ml-2 group relative">
                        <Info className={`w-4 h-4 ${isDarkMode ? "text-rp-grey/40" : "text-gray-400"} cursor-help`} />
                        <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block w-64 p-2 bg-black text-white text-xs rounded shadow-lg z-10 text-center">
                          Execution restricted between 1:30 PM and 5:00 PM PST
                          <div className="absolute left-1/2 -translate-x-1/2 top-full border-4 border-transparent border-t-black"></div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => !isRestricted && handleRunClick(jobName)}
                  disabled={isRestricted}
                  title={isRestricted ? "Job execution is restricted between 1:30 PM and 5:00 PM PST" : ""}
                  className={`flex items-center px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                    isRestricted
                      ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                      : isDarkMode
                      ? "bg-blue-600 text-rp-grey hover:bg-blue-700"
                      : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
                >
                  <Play className="w-3 h-3 mr-1.5" />
                  Run
                </button>
              </div>
            );
          })
        )}
      </div>

      {selectedJob && (
        <RunJobModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          jobName={selectedJob}
          onSuccess={handleJobSuccess}
        />
      )}
    </div>
  );
};

export default JobList;
